import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/AppHeader';
import JobDetail, { JobActions, JobStatusBanner } from '@/components/packshots/JobDetail';
import StageRail from '@/components/packshots/StageRail';
import { straightenOffer } from '@/components/packshots/StraightenControl';
import {
  compareJobs,
  countsFromJobs,
  isServiceBusyError,
  jobDisplayName,
} from '@/components/packshots/jobStatus';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  applyStageToCard,
  applyStageToJob,
  openPackshotSocket,
  stageEventSchema,
  watchAckSchema,
  type StageEvent,
} from '@/lib/packshotSocket';
import {
  approveJob,
  deleteJob,
  fetchPresets,
  getBatch,
  getJob,
  listBatchJobs,
  retryFailedBatch,
  retryJob,
  straightenJob,
  type PackshotBatch,
  type PackshotJob,
  type PackshotJobCard,
  type PackshotPresets,
  type RetryJobInput,
} from '@/lib/packshots';
import { fetchSession, hasPermission } from '@/lib/session';

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function isNewer(current: string | null, incoming: string | null): boolean {
  if (!current || !incoming) return false;
  return current > incoming;
}

export default function PackshotBatchPage() {
  const params = useParams();
  const batchId = params.id ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = searchParams.get('job');
  const [batch, setBatch] = useState<PackshotBatch | null>(null);
  const [jobs, setJobs] = useState<PackshotJobCard[] | null>(null);
  const [job, setJob] = useState<PackshotJob | null>(null);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [presets, setPresets] = useState<PackshotPresets | null>(null);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [events, setEvents] = useState<StageEvent[]>([]);
  const [liveNote, setLiveNote] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const jobIdRef = useRef(jobId);
  const requestRef = useRef(0);
  const latestEventRef = useRef<Map<string, StageEvent>>(new Map());
  jobIdRef.current = jobId;

  useEffect(() => {
    void (async () => {
      const session = await fetchSession();
      setViewerId(session?.id ?? null);
      setCanWrite(hasPermission(session, 'packshots.write'));
    })();
  }, []);

  useEffect(() => {
    if (!canWrite) return;
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchPresets();
        if (!cancelled) setPresets(next);
      } catch (err) {
        if (!cancelled) toast.error(errorText(err, 'Could not load models'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canWrite]);

  const load = useCallback(async () => {
    if (!batchId) throw new Error('Batch id is required');
    const [nextBatch, nextJobs] = await Promise.all([getBatch(batchId), listBatchJobs(batchId)]);
    return { batch: nextBatch, jobs: nextJobs };
  }, [batchId]);

  useEffect(() => {
    if (!batchId) {
      setError('Batch id is required');
      return;
    }
    let cancelled = false;
    setError(null);
    setJobs(null);
    (async () => {
      try {
        const next = await load();
        if (cancelled) return;
        setBatch(next.batch);
        setJobs(next.jobs);
      } catch (err) {
        if (!cancelled) setError(errorText(err, 'Could not load this batch'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, load]);

  useEffect(() => {
    if (!jobs) return;
    if (jobs.length === 0) {
      if (!searchParams.get('job')) return;
      const next = new URLSearchParams(searchParams);
      next.delete('job');
      setSearchParams(next, { replace: true });
      return;
    }
    const current = searchParams.get('job');
    if (current && jobs.some((item) => item.id === current)) return;
    const first = [...jobs].sort(compareJobs)[0];
    if (!first) return;
    const next = new URLSearchParams(searchParams);
    next.set('job', first.id);
    setSearchParams(next, { replace: true });
  }, [jobs, searchParams, setSearchParams]);

  useEffect(() => {
    setEvents([]);
    setLiveNote(null);
  }, [jobId]);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setJobError(null);
      setJobLoading(false);
      return;
    }
    const request = requestRef.current + 1;
    requestRef.current = request;
    let cancelled = false;
    setJobLoading(true);
    setJobError(null);
    (async () => {
      try {
        const next = await getJob(jobId);
        if (cancelled || request !== requestRef.current) return;
        const latest = latestEventRef.current.get(next.id);
        const merged = latest && (!next.updatedAt || !latest.job.updatedAt || latest.job.updatedAt >= next.updatedAt)
          ? applyStageToJob(next, latest)
          : next;
        setJob((current) => {
          if (current && current.id === merged.id && isNewer(current.updatedAt, merged.updatedAt)) return current;
          return merged;
        });
      } catch (err) {
        if (!cancelled && request === requestRef.current) setJobError(errorText(err, 'Could not load this image'));
      } finally {
        if (!cancelled && request === requestRef.current) setJobLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    const socket = openPackshotSocket();
    socketRef.current = socket;
    const onStage = (payload: unknown) => {
      const parsed = stageEventSchema.safeParse(payload);
      if (!parsed.success) return;
      const event = parsed.data;
      latestEventRef.current.set(event.jobId, event);
      setJobs((current) => current?.map((item) => applyStageToCard(item, event)) ?? current);
      setJob((current) => (current ? applyStageToJob(current, event) : current));
      if ((event.status === 'completed' || event.status === 'failed') && event.jobId === jobIdRef.current) {
        void getJob(event.jobId).then((next) => {
          setJob((current) => (current?.id === next.id ? next : current));
        }).catch((err: unknown) => {
          console.error(err instanceof Error ? err.message : 'Could not refresh model logs');
        });
      }
      setEvents((current) => {
        if (event.jobId !== jobIdRef.current) return current;
        if (event.job.status === 'queued') return [];
        return [...current, event];
      });
      setLiveNote(null);
    };
    const onConnectError = () => setLiveNote('Live updates unavailable');
    socket.on('packshot:stage', onStage);
    socket.on('connect_error', onConnectError);
    return () => {
      socket.off('packshot:stage', onStage);
      socket.off('connect_error', onConnectError);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const watchKey = jobs?.map((item) => item.id).sort().join(',') ?? '';

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !watchKey) return;
    const ids = watchKey.split(',').filter((id) => id.length > 0);
    let closed = false;
    const watchAll = () => {
      for (const id of ids) {
        socket.emit('watch', { jobId: id }, (ack: unknown) => {
          if (closed || id !== jobIdRef.current) return;
          const parsed = watchAckSchema.safeParse(ack);
          if (!parsed.success || !parsed.data.ok) setLiveNote('Live updates unavailable');
          else setLiveNote(null);
        });
      }
    };
    socket.on('connect', watchAll);
    if (socket.connected) watchAll();
    return () => {
      closed = true;
      socket.off('connect', watchAll);
      for (const id of ids) socket.emit('unwatch', { jobId: id });
    };
  }, [watchKey]);

  async function refreshSelected(id: string) {
    const next = await getJob(id);
    setJob(next);
    setJobs((current) => current?.map((item) => (item.id === next.id ? {
      ...item,
      status: next.status,
      stage: next.stage,
      productName: next.productName,
      attempts: next.attempts,
      error: next.error,
      placement: next.placement,
      files: next.files,
      updatedAt: next.updatedAt,
      startedAt: next.startedAt,
      completedAt: next.completedAt,
    } : item)) ?? current);
    setEvents([]);
  }

  async function onRetry(input: RetryJobInput) {
    if (!jobId) return;
    setPending(true);
    try {
      await retryJob(jobId, input);
      await refreshSelected(jobId);
    } catch (err) {
      toast.error(errorText(err, 'Retry failed'));
    } finally {
      setPending(false);
    }
  }

  async function onStraighten(degrees: number) {
    if (!jobId) return;
    setPending(true);
    try {
      await straightenJob(jobId, degrees);
      await refreshSelected(jobId);
    } catch (err) {
      toast.error(errorText(err, 'Could not straighten this image'));
    } finally {
      setPending(false);
    }
  }

  async function onApprove() {
    if (!jobId) return;
    setPending(true);
    try {
      await approveJob(jobId);
      await refreshSelected(jobId);
    } catch (err) {
      toast.error(errorText(err, 'Could not approve this image'));
    } finally {
      setPending(false);
    }
  }

  async function onDelete() {
    if (!jobId) return;
    setPending(true);
    try {
      await deleteJob(jobId);
      setJobs((current) => current?.filter((item) => item.id !== jobId) ?? current);
      setJob(null);
    } catch (err) {
      toast.error(errorText(err, 'Could not delete this image'));
    } finally {
      setPending(false);
    }
  }

  async function onRetryFailed() {
    setRetryingFailed(true);
    try {
      await retryFailedBatch(batchId);
      const next = await load();
      setBatch(next.batch);
      setJobs(next.jobs);
      if (jobId) await refreshSelected(jobId);
    } catch (err) {
      toast.error(errorText(err, 'Could not retry failed images'));
    } finally {
      setRetryingFailed(false);
    }
  }

  const counts = jobs ? countsFromJobs(jobs) : null;
  const selectedCard = jobId && jobs ? jobs.find((item) => item.id === jobId) ?? null : null;
  const visibleJob = job?.id === jobId ? job : null;
  const offer = straightenOffer(visibleJob);
  const railSource = visibleJob ?? selectedCard;
  const statusBanner = jobId ? (
    <JobStatusBanner job={visibleJob} card={selectedCard} />
  ) : null;
  const jobDetail = jobId ? (
    <JobDetail
      job={visibleJob}
      card={selectedCard}
      loading={jobLoading && !visibleJob && !selectedCard}
      loadError={jobError}
    />
  ) : null;

  return (
    <div className="app-shell">
      <AppHeader />
      <main className="page-frame flex flex-1 flex-col">
        <Link to="/packshots" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back
        </Link>
        {!batchId && <p className="mt-6 text-sm text-destructive">Batch id is required.</p>}
        {error && <p className="mt-6 text-sm text-destructive">{error}</p>}
        {batchId && !error && jobs === null && (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-80 w-full" />
          </div>
        )}
        {counts && jobs && (
          <>
            <div className="mt-4 flex items-center justify-between gap-4">
              <h1 className="min-w-0 text-2xl font-semibold">
                {railSource ? jobDisplayName(railSource) : 'Image'}
              </h1>
              <JobActions
                job={visibleJob}
                card={selectedCard}
                canWrite={canWrite}
                viewerId={viewerId}
                presets={presets}
                pending={pending}
                onRetry={(input) => { void onRetry(input); }}
                onApprove={() => { void onApprove(); }}
                onDelete={() => { void onDelete(); }}
              />
            </div>
            {railSource && (
              <div className="mt-6">
                <StageRail
                  job={{
                    id: railSource.id,
                    status: isServiceBusyError(railSource.error?.message) ? 'queued' : railSource.status,
                    stage: railSource.stage,
                    updatedAt: railSource.updatedAt,
                    errorStage: isServiceBusyError(railSource.error?.message) ? null : railSource.error?.stage ?? null,
                    files: railSource.files,
                    sourceMeta: visibleJob?.sourceMeta ?? null,
                  }}
                  cleanupPolicy={batch?.cleanupPolicy ?? 'auto'}
                  events={events}
                  liveNote={liveNote}
                  straighten={offer ? {
                    lean: offer.lean,
                    applied: offer.applied,
                    canWrite,
                    pending,
                    onApply: (degrees) => { void onStraighten(degrees); },
                  } : null}
                  notice={statusBanner}
                >
                  {jobDetail}
                </StageRail>
              </div>
            )}
            {jobId && jobLoading && !railSource && <Skeleton className="mt-6 h-80 w-full" />}
            {canWrite && counts.failed > 0 && (
              <Button
                type="button"
                variant="link"
                className="mt-2 h-auto justify-start px-0"
                disabled={retryingFailed}
                onClick={() => { void onRetryFailed(); }}
              >
                Retry failed
              </Button>
            )}
            {jobs.length === 0 && <p className="mt-6 text-sm text-muted-foreground">No images yet.</p>}
            {jobId && !railSource && <div className="mt-6">{jobDetail}</div>}
          </>
        )}
      </main>
    </div>
  );
}
